import type { ColumnDef, Row } from "@tanstack/react-table";

import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, FormInput, Heading, IconBin, IconPlus, Table, Text } from "@sentricmusic/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { AlbumSuggestion } from "@/api/queries";

import { useUpdateSuggestion } from "@/api/mutations";
import { apiCall } from "@/api/shared";
import { useUIStateStore } from "@/store/uiStateStore";

import type { BackendAlbumResponse } from "./spotify/backendMappers";

import useMultiSongStore from "./album/store/multiSongStore";
import { useWizardStore } from "./album/store/wizardStore";
import { mapBackendAlbum, mapBackendTrack } from "./spotify/backendMappers";

type AlbumSuggestionsTableProps = {
  data: AlbumSuggestion[];
  highlightColour?: string;
  spotifyArtistId?: string;
  isLoading?: boolean;
};

type CellProps = {
  row: Row<AlbumSuggestion>;
};

function AlbumTitleCell({ row }: CellProps) {
  const image = row.original.artwork_url;
  const title = row.original.album_title;

  return (
    <div className="flex items-center gap-3">
      {image && (
        <div className="w-10 h-10 shrink-0">
          <img
            alt="album artwork"
            title="album artwork"
            src={image}
            className="object-cover w-10 h-10 rounded"
          />
        </div>
      )}
      <span className="flex-1">{title}</span>
    </div>
  );
}

function ReleaseDateCell({ row }: CellProps) {
  return row.original.release_date
    ? format(new Date(row.original.release_date), "yyyy-MM-dd")
    : null;
}

function TracksCell({ row }: CellProps) {
  const count = row.original.total_tracks;
  return count ? `${count} ${count === 1 ? "track" : "tracks"}` : null;
}

function AlbumTypeCell({ row }: CellProps) {
  const type = row.original.album_type;
  // Capitalize first letter
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : null;
}

type AlbumActionsCellProps = CellProps & {
  onAddAlbum: (albumId: string) => void;
  onDismissAlbum: (album: AlbumSuggestion) => void;
};

function AlbumActionsCell({ row, onAddAlbum, onDismissAlbum }: AlbumActionsCellProps) {
  const { t } = useTranslation(["album"]);
  const flags = useFlags();

  return (
    <div className="flex justify-end gap-2 p-2">
      {flags["pd-add-album-feature"] === true && (
        <Button
          variant="iconAction"
          label={<IconPlus size="md" className="stroke-ink-primary" />}
          aria-label={String(t("album:add-album", "Add Album"))}
          tooltip
          tooltipText={String(t("album:add-album", "Add Album"))}
          onClick={() => void onAddAlbum(row.original.spotify_album_id)}
        />
      )}
      <Button
        variant="iconAction"
        label={<IconBin size="md" className="stroke-ink-primary" />}
        aria-label={String(t("album:dismiss-album", "Remove Suggestion"))}
        tooltip
        tooltipText={String(t("album:dismiss-album", "Remove Suggestion"))}
        onClick={() => onDismissAlbum(row.original)}
      />
    </div>
  );
}

function AlbumSuggestionsTable({ data, highlightColour, spotifyArtistId, isLoading }: AlbumSuggestionsTableProps) {
  const { t } = useTranslation(["songs", "common", "album"]);
  const navigate = useNavigate();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { setSourceData, setTracks } = useMultiSongStore();
  const { goToStep, markStepComplete, resetWizard } = useWizardStore();
  const queryClient = useQueryClient();
  const updateSuggestionMutation = useUpdateSuggestion();

  // State for delete confirmation dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [albumToDelete, setAlbumToDelete] = useState<AlbumSuggestion | null>(null);
  const [isDismissing, setIsDismissing] = useState(false);
  const [dismissProgress, setDismissProgress] = useState({ current: 0, total: 0 });
  const [albumTitleFilter, setAlbumTitleFilter] = useState("");

  // Handler to open delete confirmation dialog
  const handleDismissAlbum = useCallback((album: AlbumSuggestion) => {
    setAlbumToDelete(album);
    setIsDeleteDialogOpen(true);
  }, []);

  // Handler to confirm dismissal - fetch tracks via backend and update their suggestion status
  const confirmDismissAlbum = useCallback(async () => {
    if (!albumToDelete)
      return;

    setIsDismissing(true);
    setDismissProgress({ current: 0, total: 0 });

    try {
      const response = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/spotify/album/${albumToDelete.spotify_album_id}`,
      }) as BackendAlbumResponse;

      const allTracks = response.album.tracks ?? [];

      // Set total for progress tracking
      setDismissProgress({ current: 0, total: allTracks.length });

      // Update each track's suggestion status to dismissed (status: 2)
      let processedCount = 0;
      for (const track of allTracks) {
        if (track.id) {
          await new Promise<void>((resolve) => {
            updateSuggestionMutation.mutate(
              { spotify_song_id: track.id, status: 2 }, // status 2 = dismissed
              {
                onSuccess: () => {
                  processedCount++;
                  setDismissProgress(prev => ({ ...prev, current: processedCount }));
                  resolve();
                },
                onError: () => {
                  console.warn(`Failed to dismiss suggestion for track ${track.id}`);
                  processedCount++;
                  setDismissProgress(prev => ({ ...prev, current: processedCount }));
                  resolve();
                },
              },
            );
          });
        }
      }

      // Invalidate album suggestions query to refresh the list - await to ensure it completes
      await queryClient.invalidateQueries({ queryKey: ["album-suggestions", spotifyArtistId] });

      toast.success(t("album:album-dismissed") ?? "Album suggestion removed");
      setIsDeleteDialogOpen(false);
      setAlbumToDelete(null);
    }
    catch (error) {
      console.error("Error dismissing album:", error);
      toast.error(error instanceof Error ? error.message : "Failed to remove album suggestion");
    }
    finally {
      setIsDismissing(false);
      setDismissProgress({ current: 0, total: 0 });
    }
  }, [albumToDelete, updateSuggestionMutation, queryClient, spotifyArtistId, t]);

  // Function to fetch album data via backend and navigate to select songs step
  const handleAddAlbum = useCallback(async (spotifyAlbumId: string) => {
    try {
      toast.loading("Loading album...");

      const response = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/spotify/album/${spotifyAlbumId}`,
      }) as BackendAlbumResponse;

      const albumInfoData = mapBackendAlbum(response.album);
      const tracks = (response.album.tracks ?? []).map(mapBackendTrack);

      setSourceData(albumInfoData);
      setTracks(tracks);

      resetWizard();
      markStepComplete("import");
      goToStep("select-songs");

      void navigate({ to: "/songs/create-album" });

      toast.dismiss();
      toast.success("Album loaded successfully!");
    }
    catch (error) {
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : "Failed to load album");
      console.error("Error loading album:", error);
    }
  }, [setSourceData, setTracks, goToStep, markStepComplete, resetWizard, navigate]);

  // Memoize the cell renderer function to avoid nested component definition
  const renderActionsCell = React.useCallback(
    ({ row }: CellProps) => (
      <AlbumActionsCell
        row={row}
        onAddAlbum={(albumId: string) => {
          void handleAddAlbum(albumId);
        }}
        onDismissAlbum={handleDismissAlbum}
      />
    ),
    [handleAddAlbum, handleDismissAlbum],
  );

  const columns = useMemo((): ColumnDef<AlbumSuggestion>[] => [
    {
      id: "album_title",
      accessorKey: "album_title",
      header: String(t("songs:album-title-col-header", "Album Title")),
      size: 300,
      cell: ({ row }) => <AlbumTitleCell row={row} />,
    },
    {
      id: "album_type",
      accessorKey: "album_type",
      header: String(t("songs:album-type-col-header", "Album Type")),
      size: 150,
      cell: ({ row }) => <AlbumTypeCell row={row} />,
    },
    {
      id: "release_date",
      accessorKey: "release_date",
      header: String(t("songs:release-date")),
      size: 150,
      cell: ({ row }) => <ReleaseDateCell row={row} />,
    },
    {
      id: "total_tracks",
      accessorKey: "total_tracks",
      header: String(t("songs:album-tracks-col-header", "Tracks")),
      size: 120,
      cell: ({ row }) => <TracksCell row={row} />,
    },
    {
      id: "spotify_album_id",
      header: "",
      size: 140,
      minSize: 140,
      maxSize: 140,
      enableSorting: false,
      enablePinning: true,
      meta: { pinSide: "right" },
      cell: renderActionsCell,
    },
  ], [t, renderActionsCell]);

  const filteredAlbums = useMemo(() => {
    const sorted = [...data].sort(
      (a, b) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime(),
    );
    if (albumTitleFilter === "")
      return sorted;
    return sorted.filter(album => album.album_title.toLowerCase().includes(albumTitleFilter.toLowerCase()));
  }, [data, albumTitleFilter]);

  const filterContent = (
    <FormInput
      smallFont
      label={String(t("songs:album-title-col-header", "Album Title"))}
      placeholder={String(t("common:search", "Search"))}
      value={albumTitleFilter}
      onChange={e => setAlbumTitleFilter(e.target.value)}
    />
  );

  return (
    <>
      <Table
        enableColumnResizing={false}
        data={filteredAlbums}
        defaultPageSize={10}
        columns={columns}
        highlightColour={highlightColour}
        filterContent={filterContent}
        onClear={() => setAlbumTitleFilter("")}
        getRowId={row => row.spotify_album_id}
        noDataMessage="No album suggestions found"
        isLoading={isLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          // Prevent closing modal while dismissing
          if (!open && !isDismissing) {
            setIsDeleteDialogOpen(false);
            setAlbumToDelete(null);
          }
        }}
      >
        <DialogContent className={isTunecore ? "bg-layer-inverse text-white border-canvas-inverse" : ""}>
          <DialogHeader>
            <DialogTitle asChild>
              <Heading as="h3">
                {isDismissing
                  ? (t("album:dismissing-album") ?? "Removing Suggestion...")
                  : (t("album:confirm-dismiss") ?? "Remove Album Suggestion")}
              </Heading>
            </DialogTitle>
            <DialogDescription asChild>
              {isDismissing
                ? (
                    <div className="space-y-3">
                      <Text as="p">
                        {t("album:dismissing-tracks", { current: dismissProgress.current, total: dismissProgress.total })
                          ?? `Processing ${dismissProgress.current} of ${dismissProgress.total} tracks...`}
                      </Text>
                      {/* Progress bar */}
                      <div className="w-full h-2 overflow-hidden rounded-full bg-layer-raised">
                        <div
                          className="h-full transition-all duration-300 ease-out bg-action-primary"
                          style={{
                            width: dismissProgress.total > 0
                              ? `${(dismissProgress.current / dismissProgress.total) * 100}%`
                              : "0%",
                          }}
                        />
                      </div>
                    </div>
                  )
                : (
                    <Text as="p">
                      {t("album:dismiss-album-confirmation", { albumTitle: albumToDelete?.album_title })
                        ?? `Are you sure you want to remove "${albumToDelete?.album_title ?? "this album"}" from your suggestions? This will remove all ${albumToDelete?.total_tracks ?? 0} tracks.`}
                    </Text>
                  )}
            </DialogDescription>
          </DialogHeader>
          {!isDismissing && (
            <DialogFooter className="mt-4">
              <Button
                variant={isTunecore ? "primary" : "secondary"}
                label={t("common:cancel") ?? "Cancel"}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setAlbumToDelete(null);
                }}
                disabled={isDismissing}
              />
              <Button
                variant={isTunecore ? "secondary" : "primary"}
                label={t("album:dismiss") ?? "Remove"}
                onClick={() => void confirmDismissAlbum()}
                disabled={isDismissing || !albumToDelete}
              />
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AlbumSuggestionsTable;
